// Thin GTM/dataLayer helper — LEA-319 funnel instrumentation.
//
// The GTM container (GTM-WHFS2RCH) is booted in apps/web/index.html and
// owns `window.dataLayer`. Before this file there were zero `dataLayer.push`
// call sites in the app, so the forced onboarding gate — our weakest funnel
// step — was completely unmeasured. These three events let GTM (and whatever
// it forwards to) finally see signup → trial-start drop-off:
//
//   gate_viewed              { variant, source, lang }   — on gate mount
//   plan_cta_clicked         { tier, variant, from }     — on plan CTA click (from: 'cta' | 'trial-card')
//   checkout_session_created { tier | kind }             — after Stripe URL returns
//
// LEA-379 Prism funnel (fired from features/prism/PrismShell.tsx + main.tsx):
//   prism_target_submitted   { via }                              — audience submitted
//   prism_website_submitted  { hasWebsite }                       — website step passed
//   prism_question_answered  { step }                             — each of the 3 AI questions
//   prism_search_completed   { realCount, engineWeak, source, ms }— results reached (key signal)
//   prism_unlock_clicked     { from }                             — buy button on the result list
//                                                                   (from: results | lead = locked contact)
//   prism_paywall_viewed     {}                                   — long offer page opened
//   prism_checkout_opened    { plan, price, from }                — checkout overlay opened
//   prism_payment_submitted  {}                                   — real tripwire redirect fired
//   checkout_session_created { kind:'prism_tripwire' }            — Stripe URL returned (main.tsx)
//
// LEA-379 #6 — pre-/analyze funnel (server-blind: the prismRuns doc is only
// created at /analyze, so these client events are the ONLY signal for top-of-
// funnel drop-off). All fired from PrismShell.tsx:
//   prism_funnel_viewed          { lang, demo }    — funnel mount (top denominator)
//   prism_step_viewed            { step }          — each phase entered (per-step abandon)
//   prism_target_typed           {}                — first keystroke (engaged vs bounced)
//   prism_target_validation_failed { reason }      — target rejected pre-/analyze
//   prism_analyze_started        { hasWebsite }    — billable /analyze kickoff
//   prism_reservation_expired    {}                — paywall 15-min reservation lapsed
//   prism_abandon                { lastPhase }     — page unload, where they left
//
// Event names are the contract with Janik's GTM trigger config — do not
// rename without updating the GTM container.

type DataLayerProps = Record<string, string | number | boolean | null | undefined>;

interface DataLayerWindow extends Window {
  dataLayer?: Array<Record<string, unknown>>;
}

/**
 * Push a named event onto the GTM dataLayer. No-ops safely if the dataLayer
 * isn't present (e.g. GTM blocked by an ad-blocker, or SSR/headless) so it
 * can never break the gate or the checkout flow.
 */
export function pushDataLayer(event: string, props: DataLayerProps = {}): void {
  try {
    const w = window as DataLayerWindow;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event, ...props });
  } catch {
    // Analytics must never throw into the funnel it measures.
  }
}
